import { ApiError } from "@/clients/core/ApiError";
import { AxiosError } from "axios";

/**
 * Set of error messages that should be ignored (not shown to users)
 */
export const ignoredErrors = new Set<string>([
  "Couldn't find your account.",
  "Invalid email or password",
  "That email address is taken. Please try another.",
  "User not found",
  "You're already signed in.",
]);

/**
 * Regex to detect HTML/markup in strings
 */
const markupRegex = /<[^>]+>/i;

/**
 * Maximum length for toast messages before truncation
 */
const MAX_TOAST_MESSAGE_LENGTH = 120;

/**
 * Strips HTML markup from a string
 * @param value - String potentially containing HTML
 * @returns Clean string with markup removed
 */
export const stripMarkup = (value: string): string => {
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

/**
 * Normalises error messages from various error types
 * Handles ApiError, AxiosError, Error, and string types
 * Strips HTML markup if present
 *
 * @param error - Error of unknown type
 * @returns Normalised error message or undefined
 */
export const normalizeErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof ApiError) {
    const detail = error.body?.detail;
    const rawMessage =
      typeof detail === "string"
        ? detail
        : typeof error.body === "string"
          ? error.body
          : error.message;

    if (rawMessage && markupRegex.test(rawMessage)) {
      return stripMarkup(rawMessage) || "Unexpected server response.";
    }

    return rawMessage;
  }

  if (error instanceof AxiosError) {
    const dataMessage =
      typeof error.response?.data === "string" ? error.response?.data : error.message;
    if (dataMessage && markupRegex.test(dataMessage)) {
      return stripMarkup(dataMessage) || "Unexpected server response.";
    }
    return error.message;
  }

  if (error instanceof Error) {
    if (markupRegex.test(error.message)) {
      return stripMarkup(error.message) || "Unexpected server response.";
    }
    return error.message;
  }

  if (typeof error === "string") {
    if (markupRegex.test(error)) {
      return stripMarkup(error) || "Unexpected server response.";
    }
    return error;
  }

  return undefined;
};

/**
 * Sanitises and truncates toast messages
 * Removes HTML markup, normalises whitespace, and enforces max length
 *
 * @param message - Raw message string
 * @returns Sanitised message ready for display in toast
 */
export const sanitizeToastMessage = (
  message: string,
  options?: {
    maxLength?: number;
  },
): string => {
  const normalized = markupRegex.test(message) ? message.replace(/<[^>]*>/g, " ") : message;
  const trimmed = normalized
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join("\n");

  const maxLength = options?.maxLength ?? MAX_TOAST_MESSAGE_LENGTH;

  if (!trimmed) {
    return "Something went wrong.";
  }
  if (!Number.isFinite(maxLength) || trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, Math.max(0, maxLength - 1))}…`;
};
